import { useCallback, useMemo, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useAuth } from '@shared/components/AuthProvider';
import { GetMeDocument } from '@shared/components/AuthProvider/AuthProvider.graphql.generated';
import type { GetMeQuery, GetMeQueryVariables } from '@shared/components/AuthProvider/AuthProvider.graphql.generated';
import { useVerifyAccountEmailMutation } from '@graphql/mutations/user.graphql.generated';
import { useEmailVerificationGate } from '@shared/hooks/useEmailVerificationGate';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { noop } from '@shared/utils/noop';
import { config } from '@static/js/env.config';
import type { EmailVerificationDialogProps, EmailVerificationStep } from './EmailVerificationDialog.types';

type EnsureEmailVerified = () => Promise<boolean>;

// Return the user to a canonical /account/* URL on the auth origin after Auth0
// verifies their email. Passing the raw publish-page URL (e.g. the guest site)
// is not an allowlisted Auth0 callback and bounces the user to the login page.
const VERIFY_EMAIL_RETURN_URL = config.clientUri + '/account/personal-info?dialog=verifyEmailSuccess';

/**
 * Gates an action (e.g. website publish) behind email verification.
 *
 * Call `ensureEmailVerified()` before the gated action: it resolves `true`
 * immediately when the email is already verified, otherwise it opens the
 * verification dialog and resolves `true` once the user confirms a verified
 * status, or `false` if they dismiss it. Render `<EmailVerificationDialog {...dialogProps} />`
 * somewhere in the consuming tree.
 */
export const useEmailVerificationDialog = () => {
  const { currentUser, identifyCurrentUser } = useAuth();
  const { isEmailVerified } = useEmailVerificationGate();
  const [isPublishEmailVerificationEnabled] = useIsFeatureEnabled('publishEmailVerificationEnabled');
  const email = currentUser.profile?.email ?? undefined;

  const client = useApolloClient();
  const [sendVerifyEmail, { loading: isSending }] = useVerifyAccountEmailMutation();

  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<EmailVerificationStep>('intro');
  const [isChecking, setIsChecking] = useState(false);
  const [checkFailed, setCheckFailed] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);

  // Resolves the promise returned by the active ensureEmailVerified() call.
  const resolveRef = useRef<(verified: boolean) => void>(noop);

  const settle = useCallback((verified: boolean) => {
    setIsOpen(false);
    resolveRef.current(verified);
    resolveRef.current = noop;
  }, []);

  const ensureEmailVerified = useCallback<EnsureEmailVerified>(() => {
    // When the feature flag is off, skip the gate entirely so publishing proceeds as before.
    if (!isPublishEmailVerificationEnabled || isEmailVerified) {
      return Promise.resolve(true);
    }
    setStep('intro');
    setCheckFailed(false);
    setSendFailed(false);
    setIsOpen(true);
    return new Promise<boolean>(resolve => {
      resolveRef.current = resolve;
    });
  }, [isEmailVerified, isPublishEmailVerificationEnabled]);

  const sendEmail = useCallback(() => {
    return sendVerifyEmail({ variables: { payload: { returnUrl: VERIFY_EMAIL_RETURN_URL } } });
  }, [sendVerifyEmail]);

  const handleVerify = useCallback(async () => {
    setCheckFailed(false);
    setSendFailed(false);
    try {
      await sendEmail();
      setStep('sent');
    } catch {
      // Stay on the intro step and surface the failure so the user can retry.
      setSendFailed(true);
    }
  }, [sendEmail]);

  const handleResend = useCallback(async () => {
    setCheckFailed(false);
    setSendFailed(false);
    try {
      await sendEmail();
    } catch {
      setSendFailed(true);
    }
  }, [sendEmail]);

  const handleConfirmVerified = useCallback(async () => {
    setIsChecking(true);
    setCheckFailed(false);
    try {
      // Re-fetch verification status fresh from the server (bypassing the cache).
      const { data } = await client.query<GetMeQuery, GetMeQueryVariables>({
        query: GetMeDocument,
        variables: { withActiveEventUser: false },
        fetchPolicy: 'no-cache',
      });
      const verified = data?.me?.emailIsVerified ?? false;
      if (verified) {
        identifyCurrentUser();
        settle(true);
      } else {
        setCheckFailed(true);
      }
    } catch {
      setCheckFailed(true);
    } finally {
      setIsChecking(false);
    }
  }, [client, identifyCurrentUser, settle]);

  const handleClose = useCallback(() => {
    settle(false);
  }, [settle]);

  const dialogProps = useMemo<EmailVerificationDialogProps>(
    () => ({
      isOpen,
      step,
      email,
      isSending,
      isChecking,
      checkFailed,
      sendFailed,
      onVerify: handleVerify,
      onResend: handleResend,
      onConfirmVerified: handleConfirmVerified,
      onClose: handleClose,
    }),
    [isOpen, step, email, isSending, isChecking, checkFailed, sendFailed, handleVerify, handleResend, handleConfirmVerified, handleClose],
  );

  return {
    ensureEmailVerified,
    dialogProps,
  };
};
